import type { PointsTableRow } from './definition';
import { PlacementChart, ordinal } from './PlacementChart';
import { PLACEMENT_PRESETS } from './presets';
import { IssueList, NumberInput, smallButtonClass } from './controls';
import { issuesFor, type DefinitionValidation } from './validate';
import { trimNumber } from './wording';

/**
 * §9.3 — what each finishing place is worth.
 *
 * The rules an operator will actually hit, and how each is handled:
 *
 * - **Two rows covering one place** is an error, not a merge. First match wins, so an overlap
 *   makes part of a row unreachable — a table that looks like it pays 28th to 40th and doesn't.
 * - **A gap** is a warning. Places matching no row score nothing, which is a defined answer and
 *   occasionally the intended one.
 * - **"Runs to the last paid place"** is the checkbox on the bottom row, and only the bottom row
 *   may have it: everything below an open-ended row is unreachable.
 *
 * Everything is validated as it is typed, against `validateDefinition` — the same rules the
 * server applies on write, so nothing gets as far as a rejected save.
 */
export function PlacementTableEditor({
  rows,
  onChange,
  validation,
  readOnly = false,
}: {
  rows: PointsTableRow[];
  onChange: (rows: PointsTableRow[]) => void;
  validation: DefinitionValidation;
  readOnly?: boolean;
}) {
  const lastIndex = rows.length - 1;

  const setRow = (index: number, patch: Partial<PointsTableRow>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const removeRow = (index: number) => onChange(rows.filter((_, i) => i !== index));

  /**
   * A new row picks up where the table left off — the place after the last one covered, paying
   * what the row above paid. Both are guesses an operator will overwrite, but they beat an empty
   * line: the common edit is "same again, a bit less".
   */
  const addRow = () => {
    const last = rows[lastIndex];
    const nextFrom = last ? ((last.to ?? last.from ?? 0) + 1) : 1;
    onChange([...rows, { from: nextFrom, to: nextFrom, points: last?.points ?? 0 }]);
  };

  /** The open-ended tail. Unticking it closes the row at the place it starts on. */
  const setOpenEnded = (open: boolean) => {
    const last = rows[lastIndex];
    if (!last) return;
    setRow(lastIndex, { to: open ? null : last.from });
  };

  const openEnded = rows.length > 0 && rows[lastIndex]!.to === null;
  const sectionIssues = issuesFor(validation, 'scoring').filter((i) => i.rowIndex === undefined);

  return (
    <div className="space-y-4">
      {!readOnly ? (
        <div>
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-800/45">
            Start from
          </div>
          <div className="flex flex-wrap gap-2">
            {PLACEMENT_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => onChange(preset.rows())}
                className="max-w-[15rem] border border-line bg-surface p-2.5 text-left transition hover:border-felt-700"
              >
                <span className="block text-[12px] font-semibold text-ink-950">{preset.label}</span>
                <span className="mt-0.5 block text-[10.5px] leading-snug text-ink-800/55">
                  {preset.description}
                </span>
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] text-ink-800/45">
            Choosing one replaces every row below. Nothing is saved until you save.
          </p>
        </div>
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[26rem] text-left text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.08em] text-ink-800/45">
              <th className="w-[42%] pb-1.5 font-semibold">Places</th>
              <th className="w-[26%] pb-1.5 font-semibold">Points</th>
              <th className="pb-1.5 font-semibold">Reads as</th>
              <th className="w-8 pb-1.5" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const rowIssues = issuesFor(validation, 'scoring', index);
              const bad = rowIssues.some((i) => i.severity === 'error');
              const isLast = index === lastIndex;

              return (
                <tr key={index} className="align-top">
                  <td className="py-1 pr-2">
                    <div className="flex items-center gap-1.5">
                      <NumberInput
                        value={row.from}
                        onChange={(from) => setRow(index, { from })}
                        ariaLabel={`Row ${index + 1}: first place`}
                        min={1}
                        step={1}
                        disabled={readOnly}
                        invalid={bad}
                        className="w-16"
                      />
                      <span className="text-xs text-ink-800/40">to</span>
                      {isLast && openEnded ? (
                        <span className="text-xs text-ink-800/55">last paid place</span>
                      ) : (
                        <NumberInput
                          value={row.to}
                          onChange={(to) => setRow(index, { to })}
                          ariaLabel={`Row ${index + 1}: last place`}
                          min={1}
                          step={1}
                          disabled={readOnly}
                          invalid={bad}
                          className="w-16"
                        />
                      )}
                    </div>
                  </td>
                  <td className="py-1 pr-2">
                    <NumberInput
                      value={row.points}
                      onChange={(points) => setRow(index, { points })}
                      ariaLabel={`Row ${index + 1}: points`}
                      min={0}
                      step={0.5}
                      disabled={readOnly}
                      invalid={bad}
                      className="w-20"
                    />
                  </td>
                  <td className="py-2 pr-2 text-[11.5px] text-ink-800/55">{readsAs(row)}</td>
                  <td className="py-1">
                    {!readOnly && rows.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => removeRow(index)}
                        aria-label={`Remove row ${index + 1}`}
                        className="px-1 text-sm text-ink-800/40 transition hover:text-bad"
                      >
                        ×
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Row-level problems are listed under the table rather than beside each box: the boxes are
          narrow, and a message wrapping inside one pushes every row below it out of line. The
          offending inputs carry the error border and aria-invalid. */}
      <IssueList issues={issuesFor(validation, 'scoring').filter((i) => i.rowIndex !== undefined)} />
      <IssueList issues={sectionIssues} />

      {!readOnly ? (
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={addRow} className={smallButtonClass}>
            Add row
          </button>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-ink-800/70">
            <input
              type="checkbox"
              checked={openEnded}
              onChange={(e) => setOpenEnded(e.target.checked)}
              disabled={rows.length === 0}
              className="accent-[var(--color-felt-700)]"
            />
            Last row runs to the final paid place
          </label>
        </div>
      ) : null}

      <PlacementChart rows={rows} />
    </div>
  );
}

/** The row in words. Cheap, and it catches a transposed from/to faster than the numbers do. */
function readsAs(row: PointsTableRow): string {
  if (row.from === null || row.points === null) return '—';

  const points = trimNumber(row.points);
  if (row.to === null) return `${ordinal(row.from)} and below: ${points}`;
  if (row.to === row.from) return `${ordinal(row.from)}: ${points}`;
  return `${ordinal(row.from)}–${ordinal(row.to)}: ${points} each`;
}
